import React, { useState } from 'react'
import * as XLSX from 'xlsx'
import * as XlsxPopulate from 'xlsx-populate/browser/xlsx-populate'
import { Image, OverlayTrigger, Tooltip } from 'react-bootstrap'
import downloadIcon from "../../assets/downloadIcon.svg"
import { MemoryUtils, ExcelOptimizer, PerformanceTracker } from './ExcelOptimizationUtils'
import "./ExcelDownload.css"

const ChunkedExcelDownload = (props) => {
  const [isDownloading, setIsDownloading] = useState(false)
  const [downloadProgress, setDownloadProgress] = useState(0)

  const CHUNK_SIZE = 5000 // Process 5000 records at a time

  async function createDownLoadData() {
    if (props.data && props.data.length > 0) {
      // Validate data size and show warnings
      const warnings = ExcelOptimizer.validateDataSize(props.data.length)
      if (warnings.length > 0) {
        const proceed = window.confirm(
          `Warning: ${warnings.join(' ')} Do you want to proceed?`
        )
        if (!proceed) return
      }

      setIsDownloading(true)
      setDownloadProgress(0)
      PerformanceTracker.start('excel-export')

      try {
        // Calculate optimal chunk size based on available memory
        const optimalChunkSize = MemoryUtils.calculateOptimalChunkSize(props.data.length)
        
        if (props.data.length > 10000) {
          // Use streaming approach for very large datasets
          await handleStreamingExport(props.data, optimalChunkSize)
        } else {
          // Use chunked approach for medium datasets
          await handleChunkedExport(props.data, optimalChunkSize)
        }
        
        PerformanceTracker.log('excel-export', `Exported ${props.data.length} records`)
      } catch (error) {
        console.error('Download failed:', error)
        alert(`Download failed: ${error.message}. Try reducing the number of records.`)
      } finally {
        setIsDownloading(false)
        setDownloadProgress(0)
        ExcelOptimizer.cleanupAfterChunk()
      }
    }
  }

  const handleStreamingExport = async (data, chunkSize = CHUNK_SIZE) => {
    // Create headers
    const headers = getHeaders()
    const headerRow = {}
    let columnNumber = 1
    
    for (let header of headers) {
      const key = getExcelColumnName(columnNumber)
      headerRow[key] = header.displayName
      columnNumber++
    }

    // Create workbook with just headers
    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.json_to_sheet([headerRow], { skipHeader: true })
    
    // Process data in chunks and append to worksheet
    const totalChunks = Math.ceil(data.length / chunkSize)
    
    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      const startIndex = chunkIndex * chunkSize
      const endIndex = Math.min(startIndex + chunkSize, data.length)
      const chunk = data.slice(startIndex, endIndex)
      
      // Optimize chunk data
      const optimizedChunk = ExcelOptimizer.optimizeDataForExcel(chunk, headers)
      
      // Process chunk
      const processedChunk = processDataChunk(optimizedChunk, headers)
      
      // Append to worksheet
      XLSX.utils.sheet_add_json(ws, processedChunk, { 
        skipHeader: true, 
        origin: -1 
      })
      
      // Update progress
      const progress = Math.round(((chunkIndex + 1) / totalChunks) * 100)
      setDownloadProgress(progress)
      
      // Clean up memory after every 10 chunks
      if (chunkIndex % 10 === 0) {
        ExcelOptimizer.cleanupAfterChunk()
      }
      
      // Yield control to prevent UI blocking
      await new Promise(resolve => setTimeout(resolve, 10))
    }

    XLSX.utils.book_append_sheet(wb, ws, props.fileName)
    
    // Create and download blob
    const workbookBlob = workbook2blob(wb)
    const url = URL.createObjectURL(workbookBlob)
    downloadFile(url, props.fileName + '.xlsx')
  }

  const handleChunkedExport = async (data, chunkSize = CHUNK_SIZE) => {
    const headers = getHeaders()
    const finalData = []
    
    // Add headers
    const headerRow = {}
    let columnNumber = 1
    
    for (let header of headers) {
      const key = getExcelColumnName(columnNumber)
      headerRow[key] = header.displayName
      columnNumber++
    }
    finalData.push(headerRow)

    // Process data in chunks
    const totalChunks = Math.ceil(data.length / chunkSize)
    
    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      const startIndex = chunkIndex * chunkSize
      const endIndex = Math.min(startIndex + chunkSize, data.length)
      const chunk = data.slice(startIndex, endIndex)
      
      // Optimize chunk data
      const optimizedChunk = ExcelOptimizer.optimizeDataForExcel(chunk, headers)
      
      // Process chunk
      const processedChunk = processDataChunk(optimizedChunk, headers)
      finalData.push(...processedChunk)
      
      // Update progress
      const progress = Math.round(((chunkIndex + 1) / totalChunks) * 100)
      setDownloadProgress(progress)
      
      // Clean up memory periodically
      if (chunkIndex % 5 === 0) {
        ExcelOptimizer.cleanupAfterChunk()
      }
      
      // Yield control to prevent UI blocking
      await new Promise(resolve => setTimeout(resolve, 10))
    }

    // Create workbook
    const wb = XLSX.utils.book_new()
    const sheet = XLSX.utils.json_to_sheet(finalData, { skipHeader: true })
    XLSX.utils.book_append_sheet(wb, sheet, props.fileName)

    const workbookBlob = workbook2blob(wb)
    const url = await addStyle(workbookBlob, finalData.length, getExcelColumnName(headers.length))
    downloadFile(url, props.fileName + '.xlsx')
  }

  const processDataChunk = (chunk, headers) => {
    return chunk.map(item => {
      const obj = {}
      let columnNumber = 1
      
      for (let header of headers) {
        const key = getExcelColumnName(columnNumber)
        const displayValue = header.formatter 
          ? header.formatter(item[header.columnName], header, item) 
          : item[header.columnName]
        obj[key] = displayValue || ''
        columnNumber++
      }
      
      return obj
    })
  }

  const getHeaders = () => {
    return props.headers.filter(header => 
      (props.downloadData && header.displayIndication !== undefined) || 
      (props.downloadTemplate && header.upload)
    )
  }

  const workbook2blob = (workbook) => {
    const wopts = {
      bookType: 'xlsx',
      bookSST: false,
      type: 'binary',
    }

    const wbout = XLSX.write(workbook, wopts)
    const blob = new Blob([s2ab(wbout)], {
      type: 'application/octet-stream',
    })

    return blob
  }

  const s2ab = (s) => {
    const buf = new ArrayBuffer(s.length)
    const view = new Uint8Array(buf)
    
    for (let i = 0; i !== s.length; ++i) {
      view[i] = s.charCodeAt(i)
    }

    return buf
  }

  const addStyle = async (workbookBlob, totalRows, lastColumn) => {
    try {
      const workbook = await XlsxPopulate.fromDataAsync(workbookBlob)
      
      workbook.sheets().forEach((sheet) => {
        // Basic styling
        sheet.usedRange().style({
          fontFamily: 'Calibri',
          verticalAlignment: 'center',
        })

        // Header styling
        const headerRange = `A1:${lastColumn}1`
        sheet.range(headerRange).style({
          fill: '0577b',
          border: 'thin',
          bold: true,
          fontColor: 'FFFFFF',
          horizontalAlignment: 'left',
        })

        // Auto-fit columns (limited to prevent performance issues)
        const headers = getHeaders()
        for (let i = 0; i < Math.min(headers.length, 20); i++) {
          const columnName = getExcelColumnName(i + 1)
          sheet.column(columnName).width(15) // Set fixed width for performance
        }
      })

      return workbook.outputAsync().then((workbookBlob) => URL.createObjectURL(workbookBlob))
    } catch (error) {
      console.error('Styling failed, downloading without styles:', error)
      return URL.createObjectURL(workbookBlob)
    }
  }

  const downloadFile = (url, filename) => {
    const downloadAnchorNode = document.createElement('a')
    downloadAnchorNode.setAttribute('href', url)
    downloadAnchorNode.setAttribute('download', filename)
    downloadAnchorNode.click()
    downloadAnchorNode.remove()
    URL.revokeObjectURL(url) // Clean up memory
  }

  const getTooltipText = () => {
    if (isDownloading) {
      return `Downloading... ${downloadProgress}%`
    }
    return props.data?.length > 10000 
      ? `Download ${props.data.length.toLocaleString()} records (Large dataset)` 
      : 'Download'
  }

  return (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip>{getTooltipText()}</Tooltip>}
    >
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <Image 
          src={downloadIcon}
          className={`download-btn ${isDownloading ? 'downloading' : ''}`}
          onClick={() => {
            if (!isDownloading) {
              createDownLoadData()
            }
          }}
          style={{ 
            opacity: isDownloading ? 0.6 : 1, 
            cursor: isDownloading ? 'not-allowed' : 'pointer' 
          }}
        />
        {isDownloading && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            fontSize: '8px',
            color: '#007bff',
            fontWeight: 'bold',
            pointerEvents: 'none'
          }}>
            {downloadProgress}%
          </div>
        )}
      </div>
    </OverlayTrigger>
  )
}

function getExcelColumnName(columnNumber) {
  let columnName = ""
  
  while (columnNumber > 0) {
    let rem = columnNumber % 26
    
    if (rem === 0) {
      columnName = "Z" + columnName
      columnNumber = Math.floor(columnNumber / 26) - 1
    } else {
      columnName = (String.fromCharCode((rem - 1) + 'A'.charCodeAt(0))) + columnName
      columnNumber = Math.floor(columnNumber / 26)
    }
  }
  
  return columnName
}

export default ChunkedExcelDownload